/**
 * Whether the sitewide AI assistant is open, and which of its two presentation forms — a floating
 * bubble or a docked panel — it renders as. One conversation, two presentations; this store holds
 * only the presentation state, never the conversation itself.
 *
 * React-free module store, in the shape of `sos-call-store.ts` / `robot-alerts-store.ts` /
 * `robot-positions-store.ts` — a module-level `Set<Listener>` plus a module-level snapshot — but the
 * `Listener` shape here is deliberately different from those three. They're consumed by a manual
 * `useState` + `useEffect(() => subscribeX(setState), [])` hook, so their listener receives the
 * payload directly. This store is built for `useSyncExternalStore` (a later task wires the hook), and
 * React's contract for that hook is exact: `subscribe(onStoreChange)` takes a ZERO-ARGUMENT callback
 * whose only job is "something changed, go re-read `getAssistantSnapshot()`" — React itself decides
 * whether the new snapshot differs and whether to re-render.
 *
 * That makes `getAssistantSnapshot()` load-bearing in a way a plain getter isn't: it MUST return the
 * same object reference across calls until something genuinely changes. React 18 treats a changed
 * reference as a changed value, so a `getSnapshot` that allocates a fresh object on every call makes
 * every render see "new" state and re-render again, forever — an infinite loop, not just a wasted
 * render. Every mutator below either no-ops (same reference, no notify) or replaces `state` wholesale
 * exactly once.
 *
 * `open` is session-only, in memory: whether the assistant happens to be open right now is not a
 * preference worth remembering across a reload — every other panel in this app (drawers, the table
 * drawer, the digitize panel) starts closed on a fresh load, and the assistant is no different.
 *
 * `form` — bubble vs. dock — IS a per-user preference, and persists in `localStorage`. The
 * operator-configurable ORG-WIDE default (`Organization.settings.assistant`) is a later phase; this
 * store only ever reads/writes the CURRENT user's own local choice and never calls an API.
 */

export type AssistantForm = 'bubble' | 'dock'

export interface AssistantState {
  open: boolean
  form: AssistantForm
}

const STORAGE_KEY = 'gisify.assistant.form'
const DEFAULT_FORM: AssistantForm = 'bubble'

function isAssistantForm(value: unknown): value is AssistantForm {
  return value === 'bubble' || value === 'dock'
}

/**
 * Every read is wrapped: a private window, cleared site data, or a browser configured to block site
 * data can make `localStorage` throw or silently return null, and some contexts (thumbnail capture,
 * previews) throw on the `localStorage` ACCESSOR itself, before `.getItem` is even reached. Any of
 * those, or a stored value that isn't one of the two valid forms (a future third form, hand-edited
 * storage, a stale value from a removed form), degrades to the default — never throws.
 */
function readStoredForm(): AssistantForm {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)

    return isAssistantForm(raw) ? raw : DEFAULT_FORM
  } catch {
    return DEFAULT_FORM
  }
}

/**
 * Best-effort persist. A failed write just means the preference resets to the default next load —
 * never worth taking the assistant down for.
 */
function writeStoredForm(form: AssistantForm): void {
  try {
    localStorage.setItem(STORAGE_KEY, form)
  } catch {
    // Ignored — see the module note.
  }
}

/** React's `useSyncExternalStore` subscribe contract: a zero-argument callback, called after any
 *  change, whose only job is to tell the caller to re-read {@link getAssistantSnapshot}. */
type Listener = () => void

const listeners = new Set<Listener>()

let state: AssistantState = { open: false, form: readStoredForm() }

function notify(): void {
  listeners.forEach(l => l())
}

/** Subscribe to changes. Returns an unsubscribe function. */
export function subscribeAssistant(listener: Listener): () => void {
  listeners.add(listener)

  return () => {
    listeners.delete(listener)
  }
}

/**
 * The current state, by reference. Returns the SAME object across calls whenever nothing has
 * changed — see the module note above for why that stability is load-bearing.
 */
export function getAssistantSnapshot(): AssistantState {
  return state
}

/** Open or close the assistant. No-ops (no new reference, no notify) when already at that value. */
export function setAssistantOpen(open: boolean): void {
  if (state.open === open) return
  state = { ...state, open }
  notify()
}

/** Switch presentation form and persist it as this user's preference. No-ops when already that
 *  form. */
export function setAssistantForm(form: AssistantForm): void {
  if (state.form === form) return
  state = { ...state, form }
  writeStoredForm(form)
  notify()
}

/**
 * Test-only reset. Re-reads `localStorage` (rather than hardcoding the in-memory default) so a test
 * can set up a stored value or a throwing mock BEFORE calling this, then assert on the result.
 */
export function __resetAssistantForTests(): void {
  listeners.clear()
  state = { open: false, form: readStoredForm() }
}
